import { useCallback, useEffect, useState } from 'react';
import { CARDS_PER_PAGE, PROGRAMMATIC_SCROLL_TOLERANCE_PX, SCROLL_STEP_PX } from './scrollControl.constants';
import { ScrollGestureState, clamp } from './scrollGestureState';

// The tolerance check stops index and scrollLeft fighting: `scroll` fires all the way to a
// programmatic target, and reading the index mid-flight would drag the dots backwards.
export const useScrollPaging = (
  scrollContainerRef: React.RefObject<HTMLDivElement>,
  { programmatic }: ScrollGestureState,
  itemCount: number,
) => {
  const [startIndex, setStartIndex] = useState(0);
  const maxIndex = Math.max(0, itemCount - CARDS_PER_PAGE);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    const programmaticScroll = programmatic.current;

    const handleScroll = () => {
      if (programmaticScroll.isActive) {
        const clampedTargetIndex = clamp(programmaticScroll.targetIndex, 0, maxIndex);
        programmaticScroll.targetIndex = clampedTargetIndex;

        const targetScrollLeft = clampedTargetIndex * SCROLL_STEP_PX;
        if (Math.abs(scrollContainer.scrollLeft - targetScrollLeft) > PROGRAMMATIC_SCROLL_TOLERANCE_PX) {
          return;
        }
        programmaticScroll.isActive = false;
      }

      const nextIndex = clamp(Math.round(scrollContainer.scrollLeft / SCROLL_STEP_PX), 0, maxIndex);
      setStartIndex((prevIndex) => (prevIndex === nextIndex ? prevIndex : nextIndex));
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    // Run once, so the dots match an already-scrolled container.
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [maxIndex, scrollContainerRef, programmatic]);

  const scrollToIndex = useCallback(
    (nextIndex: number) => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) {
        return;
      }

      const clampedIndex = clamp(nextIndex, 0, maxIndex);
      const programmaticScroll = programmatic.current;
      programmaticScroll.isActive = true;
      programmaticScroll.targetIndex = clampedIndex;
      scrollContainer.scrollLeft = clampedIndex * SCROLL_STEP_PX;
      setStartIndex(clampedIndex);
    },
    [maxIndex, scrollContainerRef, programmatic],
  );

  return { startIndex, scrollToIndex };
};
